import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import IconButton from '@mui/material/IconButton'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import StarIcon from '@mui/icons-material/Star'
import { listRoles, createRole, deleteRole, seedRoles, type Role } from '../../services/authApi'

interface RolesTabProps {
  rdId: string
}

export function RolesTab({ rdId }: RolesTabProps) {
  const [roles, setRoles] = useState<Role[]>([])
  const [error, setError] = useState('')
  const [createOpen, setCreateOpen] = useState(false)

  const load = useCallback(async () => {
    try {
      setRoles(await listRoles(rdId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load roles')
    }
  }, [rdId])

  useEffect(() => { load() }, [load])

  async function handleDelete(roleId: string) {
    if (!confirm('Delete this role? This will also delete all associated policies.')) return
    try {
      await deleteRole(rdId, roleId)
      load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete role')
    }
  }

  async function handleSeed() {
    try {
      await seedRoles(rdId)
      load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to seed roles')
    }
  }

  const builtIn = ['owner', 'admin', 'analyst', 'viewer']

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="text" onClick={handleSeed}>Re-seed Defaults</Button>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New Role
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider' }} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Default</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map(r => (
              <TableRow key={r.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <strong>{r.name}</strong>
                    {builtIn.includes(r.name) && <Chip label="built-in" size="small" variant="outlined" />}
                  </Box>
                </TableCell>
                <TableCell>{r.description || '—'}</TableCell>
                <TableCell>
                  {r.isDefault ? <StarIcon fontSize="small" color="warning" /> : '—'}
                </TableCell>
                <TableCell align="right">
                  {!builtIn.includes(r.name) && (
                    <IconButton size="small" color="error" onClick={() => handleDelete(r.id)} title="Delete role">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateRoleDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); load() }}
        rdId={rdId}
      />
    </Box>
  )
}

function CreateRoleDialog({ open, onClose, onCreated, rdId }: {
  open: boolean; onClose: () => void; onCreated: () => void; rdId: string
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleCreate() {
    setError('')
    setLoading(true)
    try {
      await createRole(rdId, { name, description })
      setName('')
      setDescription('')
      onCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create role')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Role</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          fullWidth label="Role Name" value={name}
          onChange={e => setName(e.target.value)}
          helperText="Alphanumeric, hyphens, underscores. e.g. senior-analyst"
          sx={{ mt: 1, mb: 2 }}
        />
        <TextField
          fullWidth label="Description" value={description}
          onChange={e => setDescription(e.target.value)}
          multiline rows={2}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate} disabled={loading || !name}>
          {loading ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
